import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.jsx";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { formatOktaConnectSuccessLine } from "../utils/oktaAssessmentScopeBanner.js";

function normalizeOrgInput(raw) {
  if (!raw || typeof raw !== "string") return "";
  const t = raw.trim();
  if (!t) return "";
  const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withProto);
    u.hash = "";
    u.search = "";
    u.pathname = "";
    return u.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

/**
 * Modal: Okta Org URL + API token (SSWS). Probe uses server-side GET …/api/v1/users/me (same as validate-ssws).
 */
export default function ConnectOktaModal({ open, onClose, onConnectSuccess }) {
  const { validateSswsToken } = useAuth();
  const { setSession, clearSession } = useOktaSession();
  const [orgUrl, setOrgUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [successLine, setSuccessLine] = useState("");
  const [scopeWarning, setScopeWarning] = useState("");

  useEffect(() => {
    if (!open) {
      setSuccessLine("");
      setScopeWarning("");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleConnect = async () => {
    setError("");
    setSuccessLine("");
    setScopeWarning("");
    const normalized = normalizeOrgInput(orgUrl);
    const token = apiToken.trim();
    if (!normalized) {
      setError("Enter a valid Okta Org URL (e.g. https://dev-123.okta.com).");
      return;
    }
    if (!token) {
      setError("Enter your API token.");
      return;
    }

    setBusy(true);
    try {
      const caps = await validateSswsToken(normalized, token);
      setSuccessLine(formatOktaConnectSuccessLine(caps));
      if (caps?.assessmentReadiness?.level === "full") {
        setScopeWarning("This credential has more access than idwiz needs — read-only recommended.");
      }
      setSession({ orgUrl: normalized, apiToken: token });
      onConnectSuccess?.();
    } catch (e) {
      if (e?.status === 401) clearSession();
      setError(e?.message || "Could not connect. Check the org URL and token.");
    } finally {
      setBusy(false);
    }
  };

  const handleDone = () => {
    setSuccessLine("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-okta-title"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] p-6 shadow-[0_0_40px_rgb(var(--idaxis-accent-primary-rgb)/0.12)]">
        <button
          type="button"
          onClick={handleDone}
          className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-500 hover:bg-white/5 hover:text-slate-300"
          aria-label="Close"
        >
          ✕
        </button>
        <h2 id="connect-okta-title" className="text-lg font-semibold text-white">
          Connect Okta
        </h2>

        {successLine ? (
          <>
            <p className="mt-4 text-sm font-medium text-emerald-300/95">{successLine}</p>
            {scopeWarning ? (
              <p className="mt-2 text-xs text-amber-300/95">{scopeWarning}</p>
            ) : null}
            <p className="mt-2 text-xs text-slate-400">
              Token saved for this browser tab. Continue to the workspace — a scope banner appears if your role limits
              assessment coverage.
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleDone}
                className="rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] px-4 py-2.5 text-sm font-semibold text-[var(--idaxis-bg-primary)]"
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400">
              Enter your tenant <strong className="text-slate-200">Org URL</strong> and a{" "}
              <strong className="text-slate-200">SSWS API token</strong> from a{" "}
              <strong className="text-slate-200">Read-only Administrator</strong> custom role (not Super Admin).
              We verify the token against{" "}
              <code className="rounded bg-white/5 px-1 text-[11px] text-[var(--idaxis-accent-primary)]">
                /api/v1/users/me
              </code>{" "}
              before saving org URL and token in this browser tab&apos;s session storage so Command can reconnect after
              reload (same tab).
            </p>

            <label className="mt-5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Okta Org URL
            </label>
            <input
              type="url"
              value={orgUrl}
              onChange={(e) => setOrgUrl(e.target.value)}
              placeholder="https://your-domain.okta.com"
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[rgb(var(--idaxis-accent-primary-rgb)/0.4)] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--idaxis-accent-primary-rgb)/0.3)]"
              autoComplete="url"
            />

            <label className="mt-4 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              API Token
            </label>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder="SSWS API token"
              className="mt-1.5 w-full rounded-lg border border-white/10 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-2.5 text-sm font-mono text-slate-200 placeholder:text-slate-600 focus:border-[rgb(var(--idaxis-accent-primary-rgb)/0.4)] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--idaxis-accent-primary-rgb)/0.3)]"
              autoComplete="off"
            />

            {error ? <p className="mt-3 text-sm text-rose-400">{error}</p> : null}

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleDone}
                className="rounded-lg border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={handleConnect}
                className="rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] px-4 py-2.5 text-sm font-semibold text-[var(--idaxis-bg-primary)] shadow-[0_0_16px_rgb(var(--idaxis-accent-primary-rgb)/0.25)] hover:shadow-[0_0_22px_rgb(var(--idaxis-accent-primary-rgb)/0.35)] disabled:opacity-50"
              >
                {busy ? "Connecting…" : "Connect"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
